import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from 'yankees-react-utils';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import MatchupPitcherSelector from './MatchupPitcherSelector';
import InfieldPositioning from './InfieldPositioning';

const StyledInfieldReportHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: .5rem;
    > h3 {
        margin-bottom: 0;
    }
    > div {
        display: flex;
        align-items: center;
    }
`;

const BaseInfieldReport = ({ isPlayerLoading, player, playerTeamStartingPitchers, isOpposingPitcherVersion, opposingTeamID }) => {
    const { t: translate } = useTranslation();
    const userPlayer = usePlayerSelector();
    const [matchupPitcher, setMatchupPitcher] = useState(null);
    const [matchupBatter, setMatchupBatter] = useState(null);
    const isNonPitcherViewingOpponentHitters = !userPlayer.IsPitcher && !isOpposingPitcherVersion;
    useEffect(() => {
        if (player) {
            if (isNonPitcherViewingOpponentHitters) {
                setMatchupBatter(player);
                if (playerTeamStartingPitchers?.length) {
                    setMatchupPitcher({
                        ...playerTeamStartingPitchers[0],
                        name: playerTeamStartingPitchers[0].FullName,
                        value: playerTeamStartingPitchers[0].PlayerID,
                    });
                }
            } else if (userPlayer.IsPitcher) {
                setMatchupBatter(player);
                setMatchupPitcher(userPlayer);
            } else {
                setMatchupBatter(userPlayer);
                setMatchupPitcher(player);
            }
        }
    }, [player]); // eslint-disable-line react-hooks/exhaustive-deps

    // if (isPlayerLoading) return <LoadingSpinner />;
    return (
        <>
            {isPlayerLoading && <LoadingSpinner />}
            <StyledInfieldReportHeader>
                <div>
                    {isNonPitcherViewingOpponentHitters && userPlayer && userPlayer.TeamID ? (
                        <>
                            <b className="me-1">{translate('common.select-a-pitcher')}:</b>
                            <MatchupPitcherSelector
                                onSelect={setMatchupPitcher}
                            />
                        </>
                    ) : isNonPitcherViewingOpponentHitters && <>{translate('sections.opponent.player.logged-in-no-id')}</>}
                </div>
            </StyledInfieldReportHeader>
            {matchupPitcher?.PlayerID && matchupBatter?.PlayerID && (
                <InfieldPositioning
                    key={`${matchupPitcher?.PlayerID}-${matchupBatter.PlayerID}`}
                    pitcher={matchupPitcher}
                    batter={matchupBatter}
                    opposingTeamID={opposingTeamID}
                />
            )}
        </>
    );
};

BaseInfieldReport.propTypes = {
    isPlayerLoading: PropTypes.bool.isRequired,
    player: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
    }),
    playerTeamStartingPitchers: PropTypes.arrayOf(PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.string.isRequired,
    })),
    isOpposingPitcherVersion: PropTypes.bool,
    opposingTeamID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
BaseInfieldReport.defaultProps = {
    player: null,
    playerTeamStartingPitchers: undefined,
    isOpposingPitcherVersion: false,
};

export default BaseInfieldReport;
